import { useDeviceDetection } from "../../../hooks/UseDeviceDetection";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDependency } from "../../../contexts/DependencyProvider";
import { useViewInfo } from "../../../hooks/UseViewInfo";
import useScreenSize from "../../../hooks/UseScreenSize";
import moment from "moment";
import clsx from "clsx";
import {
    Flex,
    View,
    ProgressCircle,
    TableView,
    TableBody,
    TableHeader,
    Row,
    Cell,
    Column,
    Heading,
    Content,
    ListBox,
    Item,
    IllustratedMessage,
    DialogContainer,
    AlertDialog,
    Link,
} from "@adobe/react-spectrum";
import { CircleFill } from "react-bootstrap-icons";
import NotFound from "@spectrum-icons/illustrations/NotFound";
import FilterComponent from "./components/FilterComponent";
import HeaderComponent from "./components/HeaderComponent";
import { invokeFinderApp } from "../../../utils/NavigationUtils";
import CardComponent from "./components/CardComponent";
import { ServiceActivitiesLayout } from "./components/ServiceActivitiesLayout";
import { ComponentFactory } from "../../../components/protected/layout-components/ComponentFactory";
import { MobileLayout } from "./components/MobileLayout";
import { MobileListView } from "./components/MobileListView";
import { MobileDetailView } from "./components/MobileDetailView";
import {
    FPAData,
    FPADataFactory,
    FPADataTypes,
} from "../../../infra/protected/FPA/FPAData";
import { usePreloadAssets } from "../../../hooks/UsePreloadAssets";
import { RootState } from "../../../state/store";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { updateAppInfo } from "../../../state/userWebLayout/userWebLayoutSlice";
import { ListRegisterUtils } from "../../../utils/ListRegisterUtils";
import {
    AtollonButton,
    AtollonButtonDesignType,
} from "../../../components/ui/Button/AtollonButton";
import { CustomViewDialog } from "../../../components/protected/Dialogs/CustomViewDialog";
import {
    ContextNewDialog,
    ContextNewDialogMode,
} from "../../../components/protected/Context/ContextNewDialog";
import { DataProvider } from "../../../components/protected/data-components/Contexts/DataContext";
import {
    addKey,
    useKeyboardShortcuts,
} from "../../../hooks/UseKeyboardShortcuts";
import { DeleteConfirmationDialog } from "../finder/components/DeleteConfirmationDialog";
import { DeleteActivityRequest } from "../../../services/soap/project/requests/DeleteActivityRequest";
import { DeleteCustomViewsRequest } from "../../../services/soap/features/requests/DeleteCustomViewsRequest";
import { ToastQueue } from '@adobe/react-spectrum';
import {
    AtollonEvent,
    subscribe,
    unsubscribe,
} from "../../../infra/events/Events";
import { ContextMoveDialog } from "../../../components/protected/Context/ContextMoveDialog";
import { useServiceActivitiesState } from "./hooks/useServiceActivitiesState";
import { useServiceActivitiesData } from "./hooks/useServiceActivitiesData";
import { mergeColumns } from "./utils/columnMergeUtils";
import "./service_activities.css";
import styles from "./service_activities.module.css";
import { ListRegisterRequest } from "../../../services/soap/features/requests/ListRegisterRequest";
import { CreateRegisterRequest } from "../../../services/soap/features/requests/CreateRegisterRequest";
import { UpdateRegisterRequest } from "../../../services/soap/features/requests/UpdateRegisterRequest";
import {
    ActivityItem,
    ActivityStatus,
    FilterColumn,
    QueryParams,
    ServiceActivitiesDataRef,
} from "./types";
import { CustomView } from "../../../services/soap/dataObjects/CustomView";
import type { Key } from "react";
import type { Selection } from "@react-types/shared";
import type { Row as RegisterRow } from "../../../services/soap/features/responses/ListRegisterResponse";

export function ServiceActivities() {
    const { activityStatusMapping } = usePreloadAssets();
    const screenSize = useScreenSize();
    const { t } = useTranslation();
    const { isMobile } = useViewInfo();
    const deviceInfo = useDeviceDetection(isMobile);
    const { store, projectService, featureService } = useDependency();
    const dataStore = useSelector(
        (state: RootState) => state.userWebLayout.appInfos
    );
    const selectedApp = useSelector(
        (state: RootState) => state.userWebLayout.selectedAppId
    );
    const dispatch = useDispatch();
    const { state } = useLocation();
    // State for forcing re-renders when device info changes
    const [renderKey, setRenderKey] = useState(0);
    const [initialCustomViewId, setInitialCustomViewId] = useState<
        string | undefined
    >(undefined);
    const [lastViewRegisterLoaded, setLastViewRegisterLoaded] =
        useState<boolean>(false);
    const [isCreateViewDialogOpen, setIsCreateViewDialogOpen] =
        useState<boolean>(false);
    /** Edit/duplicate: which view is being edited or duplicated (null = create mode) */
    const [customViewDialogEditView, setCustomViewDialogEditView] =
        useState<CustomView | null>(null);
    /** True when the open dialog is in duplicate mode (editView is set, isDuplicate) */
    const [customViewDialogIsDuplicate, setCustomViewDialogIsDuplicate] =
        useState<boolean>(false);
    /** View pending delete confirmation */
    const [viewToDelete, setViewToDelete] = useState<CustomView | null>(null);
    const [isDeletingView, setIsDeletingView] = useState<boolean>(false);
    // Use custom hook for state management
    const { state: componentState, actions } = useServiceActivitiesState();
    const {
        activities,
        isLoading: dataLoading,
        isViewsLoading,
        error: dataError,
        hasMore,
        loadingState,
        columns,
        activityStatuses,
        activityListKey,
        formSettingId,
        formSettings,
        customViews,
        hasMoreCustomViews,
        loadMoreCustomViews,
        refreshCustomViews,
        fetchActivityLayout,
        loadMore,
        showLess,
        isExpanded,
        reload,
    } = useServiceActivitiesData(selectedApp);

    // Destructure state for easier access
    const {
        showLoader,
        showLoaderDetail,
        pageView,
        selectedKeys,
        definition,
        showMobileDetailView,
        selectedItem,
        selectedDataRow,
        showAdd,
        isMoveDialogOpen,
        isDeleteDialogOpen,
        showFilter,
    } = componentState;

    const searchedValueRef = useRef<string>("");
    const serviceActivityValueRef = useRef<string>("");
    const queryParamsRef = useRef<QueryParams>({ FILTER: { COL: [] } });
    const dataRef = useRef<ServiceActivitiesDataRef>({});

    const activeViewId =
        queryParamsRef.current?.FILTER?.COL?.find((col) => col.name === "state")
            ?.value != null
            ? String(
                  queryParamsRef.current?.FILTER?.COL?.find(
                      (col) => col.name === "state"
                  )?.value
              )
            : "";

    const fieldsSignature = Array.isArray(queryParamsRef.current.FIELDS?.field)
        ? queryParamsRef.current.FIELDS!.field.join("|")
        : "";

    // Compute merged columns (CustomView columns first, then base columns)
    const mergedColumns = useMemo(() => {
        return mergeColumns(columns, customViews, queryParamsRef.current);
    }, [columns, customViews, fieldsSignature, activeViewId]);

    const activeViewDefinition = useMemo(() => {
        if (!activeViewId) return "";
        return customViews.find((v) => String(v.id ?? "") === activeViewId)
            ?.definition ?? "";
    }, [activeViewId, customViews]);

    const visibleColumnKeySet = useMemo(() => {
        if (!activeViewId) return null;
        if (!activeViewDefinition) return null;

        try {
            const parsed = JSON.parse(activeViewDefinition) as {
                columns?: Array<{
                    itemname?: string;
                    type?: string;
                    isVisible?: boolean;
                }>;
            };

            const cols = Array.isArray(parsed?.columns) ? parsed.columns : [];
            const visibleKeys = new Set<string>();

            cols.forEach((col) => {
                // Strict: only show when isVisible === true
                if (col?.isVisible !== true) return;

                const rawItemname = String(col?.itemname ?? "").trim();
                if (!rawItemname) return;

                let key = rawItemname;
                if (rawItemname.includes("|")) {
                    const [, right] = rawItemname.split("|");
                    if (right && right.trim() !== "") {
                        key = right.trim();
                    }
                } else if (col?.type && String(col.type).includes("|")) {
                    const [, rightType] = String(col.type).split("|");
                    if (rightType && rightType.trim() !== "") {
                        key = rightType.trim();
                    }
                }

                if (key) visibleKeys.add(key);
            });

            return visibleKeys;
        } catch {
            return null;
        }
    }, [activeViewDefinition, activeViewId]);

    const visibleGridColumns = useMemo(() => {
        // No active view -> no visibility enforcement here
        if (!activeViewId) return mergedColumns;

        // If we can’t parse, fall back to whatever mergeColumns produced
        if (!visibleColumnKeySet) return mergedColumns;

        // Strict visibility: if none are visible, show none
        return mergedColumns.filter((c) => visibleColumnKeySet.has(c.key));
    }, [activeViewId, mergedColumns, visibleColumnKeySet]);

    const modeRef = useRef<ContextNewDialogMode>("new");
    const layoutRequestIdRef = useRef(0);
    const isInitializingRef = useRef<boolean>(false);
    const hasInitializedRef = useRef<boolean>(false);
    const currentAppRef = useRef<string | null>(null);
    const getLayoutRef = useRef<
        ((selectedRow: ActivityItem | null) => Promise<void>) | null
    >(null);
    const scrollToSelectedRef = useRef<(() => void) | null>(null);
    const lastAppliedCustomViewIdRef = useRef<string | null>(null);
    const lastViewIdFromRegisterRef = useRef<string | null>(null);
    const resolvedInitialViewForAppRef = useRef<string | null>(null);
    const skipNextViewPersistRef = useRef<boolean>(false);
    const lastViewRegisterRowRef = useRef<Pick<RegisterRow, "id" | "assignTo"> | null>(
        null
    );
    const persistLastViewRequestIdRef = useRef<number>(0);
    const pendingCreatedViewIdRef = useRef<string | null>(null);
    const pendingViewRetryCountRef = useRef<number>(0);
    const resetViewBaselineRef = useRef<(() => void) | null>(null);

    // Helper function to safely access FILTER.COL with default empty array
    const getFilterCol = useCallback((queryParams: QueryParams | undefined | null): FilterColumn[] => {
        return queryParams?.FILTER?.COL ?? [];
    }, []);

    const listLastViewRegisterRow = useCallback(async (): Promise<RegisterRow | null> => {
        if (!selectedApp) return null;
        try {
            const res = await featureService.listRegister(
                new ListRegisterRequest(
                    store.Server,
                    store.SessionId,
                    ListRegisterUtils.Modules.Project,
                    ListRegisterUtils.Keys.Project.LastViewServiceActivities,
                    1,
                    ListRegisterUtils.Levels.Project.Default,
                    Number(selectedApp)
                )
            );

            if (!res || res.count <= 0 || !Array.isArray(res.ROWS) || res.ROWS.length === 0) {
                return null;
            }
            return res.ROWS[0]?.ROW ?? null;
        } catch (error) {
            console.error("Error loading last view register row:", error);
            return null;
        }
    }, [featureService, selectedApp, store.Server, store.SessionId]);

    // Load persisted "last view" from Register early on app changes
    useEffect(() => {
        lastViewIdFromRegisterRef.current = null;
        lastViewRegisterRowRef.current = null;
        setLastViewRegisterLoaded(false);
        resolvedInitialViewForAppRef.current = null;
        setInitialCustomViewId(undefined);

        if (!selectedApp) {
            setLastViewRegisterLoaded(true);
            return;
        }

        (async () => {
            const row = await listLastViewRegisterRow();
            if (row?.id) {
                lastViewRegisterRowRef.current = {
                    id: row.id,
                    assignTo: row.assignTo,
                };
                lastViewIdFromRegisterRef.current =
                    row.value != null ? String(row.value) : null;
            }
            setLastViewRegisterLoaded(true);
        })();
    }, [listLastViewRegisterRow, selectedApp]);

    const persistLastViewId = useCallback(
        async (viewId: string): Promise<void> => {
            if (!selectedApp) return;
            const normalizedViewId = String(viewId ?? "").trim();
            if (!normalizedViewId) return;

            const requestId = ++persistLastViewRequestIdRef.current;

            try {
                // Prefer cached register row metadata, otherwise load it once
                let rowMeta = lastViewRegisterRowRef.current;
                if (!rowMeta) {
                    const existingRow = await listLastViewRegisterRow();
                    if (requestId !== persistLastViewRequestIdRef.current) return;
                    if (existingRow?.id) {
                        rowMeta = { id: existingRow.id, assignTo: existingRow.assignTo };
                        lastViewRegisterRowRef.current = rowMeta;
                    }
                }

                if (rowMeta?.id) {
                    await featureService.updateRegister(
                        new UpdateRegisterRequest(
                            store.Server,
                            store.SessionId,
                            rowMeta.id,
                            undefined,
                            ListRegisterUtils.Modules.Project,
                            Number(selectedApp),
                            ListRegisterUtils.Keys.Project.LastViewServiceActivities,
                            ListRegisterUtils.Levels.Project.Default,
                            normalizedViewId,
                            rowMeta.assignTo
                        )
                    );
                    return;
                }

                await featureService.createRegister(
                    new CreateRegisterRequest(
                        store.Server,
                        store.SessionId,
                        ListRegisterUtils.Modules.Project,
                        ListRegisterUtils.Keys.Project.LastViewServiceActivities,
                        ListRegisterUtils.Levels.Project.Default,
                        normalizedViewId,
                        String(selectedApp)
                    )
                );

                if (requestId !== persistLastViewRequestIdRef.current) return;

                // Fetch the created row so we can cache assignTo/id for future updates
                const createdRow = await listLastViewRegisterRow();
                if (createdRow?.id) {
                    lastViewRegisterRowRef.current = {
                        id: createdRow.id,
                        assignTo: createdRow.assignTo,
                    };
                }
            } catch (error) {
                console.error("Error persisting last view id:", error);
            }
        },
        [featureService, listLastViewRegisterRow, selectedApp, store.Server, store.SessionId]
    );

    // Helper function to validate and normalize QueryParams structure
    const normalizeQueryParams = useCallback((params: any): QueryParams => {
        if (!params || typeof params !== "object") {
            return {
                FILTER: { COL: [] },
            };
        }

        // Ensure FILTER structure exists - set it after spread to override any malformed FILTER
        const normalized: QueryParams = {
            ...params,
            FILTER: {
                COL: Array.isArray(params.FILTER?.COL) ? params.FILTER.COL : [],
            },
        };

        return normalized;
    }, []);

    /**
     * Applies a custom view selection by building query params from the view's definition.
     * This replicates the logic from FilterComponent.handleViewSelect to ensure consistency.
     *
     * @param view - The CustomView to apply
     * @returns QueryParams object with filters, fields, and sorting from the view
     */
    const applyViewSelection = useCallback(
        (view: CustomView): QueryParams => {
            // Initialize query params with empty filter columns
            const updatedParams: QueryParams = {
                FILTER: { COL: [] },
            };

            if (!view.definition) {
                // No definition, just add state filter
                updatedParams.FILTER.COL.push({
                    name: "state",
                    type: "equals",
                    value: view.id || "",
                });
                return updatedParams;
            }

            try {
                const definition = JSON.parse(view.definition);

                // Extract FIELDS from columns[].itemname (only visible columns)
                if (
                    definition.columns &&
                    Array.isArray(definition.columns)
                ) {
                    const fields = definition.columns
                        .filter((col: any) => col.isVisible !== false)
                        .map((col: any) => col.itemname)
                        .filter(
                            (itemname: any) =>
                                itemname != null && itemname !== ""
                        );

                    if (fields.length > 0) {
                        updatedParams.FIELDS = { field: fields };
                    }
                }

                // Extract ORDER from sorting[]
                if (
                    definition.sorting &&
                    Array.isArray(definition.sorting) &&
                    definition.sorting.length > 0
                ) {
                    updatedParams.ORDER = {
                        COL: definition.sorting.map((sort: any) => {
                            // If itemname contains '|', split and use the second part
                            let sortName = sort.itemname;
                            if (sortName && sortName.includes("|")) {
                                const parts = sortName.split("|");
                                if (
                                    parts.length > 1 &&
                                    parts[1]?.trim() !== ""
                                ) {
                                    sortName = parts[1].trim();
                                }
                            }
                            return {
                                name: sortName,
                                desc:
                                    sort.level === "Descending" ? "true" : "false",
                            };
                        }),
                    };
                }

                // Apply preset filter values from filters[]
                if (Array.isArray(definition.filters)) {
                    definition.filters.forEach((filterDef: any) => {
                        if (!filterDef?.filterInfo?.length) return;

                        const filterInfo = filterDef.filterInfo[0];
                        // Allow is_empty and is_not_empty operators even if value is empty
                        const isEmptyOperator =
                            filterInfo?.type === "is_empty" ||
                            filterInfo?.type === "is_not_empty";
                        // Don't skip isCurrentUser filters when value is empty - we substitute store.UserId below
                        if (
                            !isEmptyOperator &&
                            !filterInfo?.value &&
                            filterInfo?.isCurrentUser !== true
                        )
                            return;

                        // If filter name contains '|', split and use the first part
                        let filterName = filterDef.name;
                        if (filterName && filterName.includes("|")) {
                            const parts = filterName.split("|");
                            filterName = parts[0]?.trim() || filterName;
                        }

                        // Convert is_empty/is_not_empty to API format
                        let filterType = filterInfo.type || "equals";
                        let negation = false;

                        if (filterInfo.type === "is_empty") {
                            filterType = "empty";
                            negation = false;
                        } else if (filterInfo.type === "is_not_empty") {
                            filterType = "empty";
                            negation = true;
                        }

                        // Use runtime userId if isCurrentUser flag is set, otherwise use saved value
                        let filterValue = filterInfo.value || "";
                        if (
                            filterInfo.isCurrentUser === true &&
                            store
                        ) {
                            filterValue = String(store.UserId);
                        }

                        const filterParam: FilterColumn = {
                            name: filterName,
                            type: filterType as FilterColumn["type"],
                            value: filterValue,
                        };

                        // Add negation parameter if true (using a workaround since FilterColumn doesn't have negation)
                        // For now, we'll handle negation by setting the type appropriately
                        if (negation && filterType === "empty") {
                            // For empty filters, negation is handled by the type being 'empty' with negation flag
                            // Since FilterColumn doesn't support negation directly, we'll need to extend the type
                            // For now, we'll use a workaround by storing it in a way that the API can understand
                            (filterParam as any).negation = true;
                        }

                        updatedParams.FILTER.COL.push(filterParam);
                    });
                }
            } catch (error) {
                console.error("Error parsing view definition:", error);
            }

            // Remove empty filter values (but preserve is_empty/is_not_empty filters which have empty values but type='empty')
            updatedParams.FILTER.COL = updatedParams.FILTER.COL.filter(
                (col: FilterColumn) =>
                    (col as any).type === "empty" ||
                    (col.value !== "" &&
                        col.value !== null &&
                        col.value !== undefined)
            );

            // Add state filter to indicate which custom view is selected
            updatedParams.FILTER.COL.push({
                name: "state",
                type: "equals",
                value: view.id || "",
            });

            return updatedParams;
        },
        [store]
    );

    // Helper function to extract layout orientation from selected custom view
    const getLayoutOrientationFromCustomView = useCallback((): {
        desktopOrientation?: "Detail" | "Full";
        tabletOrientation?: "Detail" | "Full";
        mobileOrientation?: "Detail" | "Full"; // Deprecated, kept for backward compatibility
    } | null => {
        // Check if a custom view is selected by looking for "state" filter
        const stateFilter = getFilterCol(queryParamsRef.current).find(
            (col) => col.name === "state"
        );
        if (!stateFilter || !stateFilter.value) {
            return null;
        }

        const viewId = String(stateFilter.value);

        // Find the matching custom view
        const selectedView = customViews.find((view) => view.id === viewId);
        if (!selectedView || !selectedView.definition) {
            return null;
        }

        try {
            const definition = JSON.parse(selectedView.definition);
            // Handle backward compatibility: convert old 'table view'/'cards view' to 'Detail'/'Full'
            const desktopOrientationRaw = definition.desktopOrientation;
            const desktopOrientation: "Detail" | "Full" | undefined =
                desktopOrientationRaw === "table view" ||
                desktopOrientationRaw === "Detail"
                    ? "Detail"
                    : desktopOrientationRaw === "cards view" ||
                      desktopOrientationRaw === "Full"
                    ? "Full"
                    : undefined;

            // For backward compatibility: if tabletOrientation is not set, try tabletHorizontalOrientation, tabletVerticalOrientation, or mobileOrientation
            const mobileOrientationRaw = definition.mobileOrientation;
            const defaultTabletOrientation: "Detail" | "Full" | undefined =
                mobileOrientationRaw === "cards view" ||
                mobileOrientationRaw === "Full"
                    ? "Full"
                    : mobileOrientationRaw === "table view" ||
                      mobileOrientationRaw === "Detail"
                    ? "Detail"
                    : undefined;

            const tabletOrientationRaw =
                definition.tabletOrientation ||
                definition.tabletHorizontalOrientation ||
                definition.tabletVerticalOrientation ||
                mobileOrientationRaw;
            const tabletOrientation: "Detail" | "Full" | undefined =
                tabletOrientationRaw === "table view" ||
                tabletOrientationRaw === "Detail"
                    ? "Detail"
                    : tabletOrientationRaw === "cards view" ||
                      tabletOrientationRaw === "Full"
                    ? "Full"
                    : defaultTabletOrientation;

            return {
                desktopOrientation,
                tabletOrientation,
                mobileOrientation: defaultTabletOrientation, // Keep for backward compatibility
            };
        } catch (error) {
            console.error("Error parsing custom view definition:", error);
            return null;
        }
    }, [customViews]);

    useEffect(() => {
        if (state.appKey && dataStore[state.appKey]) {
            try {
                if (dataStore[state.appKey].pageView) {
                    dataRef.current.pageView = dataStore[state.appKey].pageView;
                    actions.setPageView(dataStore[state.appKey].pageView);
                }
                if (dataStore[state.appKey].selectedItem) {
                    const parsedSelectedItem =
                        typeof dataStore[state.appKey].selectedItem === "string"
                            ? JSON.parse(dataStore[state.appKey].selectedItem)
                            : dataStore[state.appKey].selectedItem;
                    dataRef.current.selectedItem = parsedSelectedItem;
                    actions.setSelectedItem(parsedSelectedItem);
                    actions.setSelectedKeys(new Set([parsedSelectedItem?.id]));
                    if (dataStore[state.appKey].selectedKeys) {
                        const parsedSelectedKeys =
                            typeof dataStore[state.appKey].selectedKeys ===
                            "string"
                                ? JSON.parse(
                                      dataStore[state.appKey].selectedKeys
                                  )
                                : Array.from(
                                      dataStore[state.appKey].selectedKeys || []
                                  );
                        dataRef.current.selectedKeys = new Set(
                            parsedSelectedKeys
                        );
                    }
                }
                if (dataStore[state.appKey].selectedRow) {
                    const parsedSelectedRow =
                        typeof dataStore[state.appKey].selectedRow === "string"
                            ? JSON.parse(dataStore[state.appKey].selectedRow)
                            : dataStore[state.appKey].selectedRow;
                    dataRef.current.selectedRow = parsedSelectedRow;
                    actions.setSelectedDataRow(parsedSelectedRow);
                    getLayoutRef.current?.(parsedSelectedRow);
                }
                if (dataStore[state.appKey].showMobileDetailView) {
                    dataRef.current.showMobileDetailView =
                        dataStore[state.appKey].showMobileDetailView;
                    actions.setShowMobileDetailView(
                        dataStore[state.appKey].showMobileDetailView
                    );
                }
                if (dataStore[state.appKey].queryParams) {
                    let parsedParams: any;
                    try {
                        parsedParams = typeof dataStore[state.appKey].queryParams === "string"
                            ? JSON.parse(dataStore[state.appKey].queryParams)
                            : dataStore[state.appKey].queryParams;
                    } catch (error) {
                        console.error("Error parsing queryParams from store:", error);
                        parsedParams = null;
                    }
                    const normalizedParams = normalizeQueryParams(parsedParams);
                    dataRef.current.queryParams = JSON.stringify(normalizedParams);
                    queryParamsRef.current = normalizedParams;
                }
                if (dataStore[state.appKey].settingId) {
                    dataRef.current.settingId =
                        dataStore[state.appKey].settingId;
                }
            } catch (error) {
                console.error(
                    "Error loading service activities data from store:",
                    error
                );
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.appKey, dataStore.appInfos]);

    useKeyboardShortcuts(
        [...addKey("c", { ctrl: true, shift: true }, () => handleAddNew())],
        true
    );

    useEffect(() => {
        (async () => {
            if (!selectedApp) return;

            if (
                currentAppRef.current === selectedApp &&
                hasInitializedRef.current
            ) {
                return;
            }
            if (isInitializingRef.current) {
                return;
            }

            isInitializingRef.current = true;
            currentAppRef.current = selectedApp;

            try {
                let parsedParams: any = null;
                if (dataRef.current.queryParams) {
                    try {
                        parsedParams = typeof dataRef.current.queryParams === "string"
                            ? JSON.parse(dataRef.current.queryParams)
                            : dataRef.current.queryParams;
                    } catch (error) {
                        console.error("Error parsing queryParams during initialization:", error);
                        parsedParams = null;
                    }
                }
                queryParamsRef.current = normalizeQueryParams(parsedParams);
                actions.setPageView(dataRef.current.pageView || "Detail");
                searchedValueRef.current = "";

                // Views-first: do not trigger an activity reload unless a Custom View is already selected.
                // This prevents a default/fallback fetch before the active view is resolved.
                const hasActiveView =
                    getFilterCol(queryParamsRef.current).some(
                        (col) => col.name === "state" && !!col.value
                    );
                if (hasActiveView) {
                    reload(queryParamsRef.current);
                }
                hasInitializedRef.current = true;
            } catch (error) {
                console.error("Error during initialization:", error);
            } finally {
                isInitializingRef.current = false;
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedApp]);

    /**
     * Resolve initial custom view selection after Register lookup and Custom Views load.
     *
     * Waterfall:
     * - If `updateAppInfo` cache already selected a view (state filter in queryParams), do nothing.
     * - Else if Register has a last view id and it exists, select it.
     * - Else if any views exist, select the first view (by priority asc, fallback to list order).
     * - Else: no initial view id (empty state will be shown).
     */
    useEffect(() => {
        if (!selectedApp) return;
        if (!lastViewRegisterLoaded) return;
        if (isViewsLoading) return;

        if (resolvedInitialViewForAppRef.current === selectedApp) {
            return;
        }

        const cachedStateFilter = getFilterCol(queryParamsRef.current).find(
            (col) => col.name === "state"
        );
        const cachedViewId = cachedStateFilter?.value
            ? String(cachedStateFilter.value)
            : null;

        // Same-session cache wins: do not override
        if (cachedViewId) {
            resolvedInitialViewForAppRef.current = selectedApp;
            setInitialCustomViewId(undefined);
            return;
        }

        if (!customViews || customViews.length === 0) {
            resolvedInitialViewForAppRef.current = selectedApp;
            setInitialCustomViewId(undefined);
            return;
        }

        const registerViewIdRaw = lastViewIdFromRegisterRef.current;
        const registerViewId = registerViewIdRaw
            ? String(registerViewIdRaw).trim()
            : null;
        const registerExistsInViews =
            !!registerViewId &&
            customViews.some((v) => String(v.id ?? "") === registerViewId);

        const firstView = [...customViews].sort((a, b) => {
            const pa = Number(a.priority ?? Number.POSITIVE_INFINITY);
            const pb = Number(b.priority ?? Number.POSITIVE_INFINITY);
            if (Number.isFinite(pa) && Number.isFinite(pb) && pa !== pb) {
                return pa - pb;
            }
            if (Number.isFinite(pa) && !Number.isFinite(pb)) return -1;
            if (!Number.isFinite(pa) && Number.isFinite(pb)) return 1;
            return 0;
        })[0];
        const firstViewId = firstView?.id ? String(firstView.id) : null;

        const targetViewId = registerExistsInViews
            ? registerViewId!
            : firstViewId;

        if (targetViewId) {
            // Avoid writing to Register on initial apply
            skipNextViewPersistRef.current = true;
            setInitialCustomViewId(targetViewId);
        } else {
            setInitialCustomViewId(undefined);
        }

        resolvedInitialViewForAppRef.current = selectedApp;
    }, [
        customViews,
        isViewsLoading,
        getFilterCol,
        lastViewRegisterLoaded,
        selectedApp,
    ]);

    /**
     * Handles context app creation events (copy/move operations).
     *
     * @param event - The AtollonEvent containing operation details
     */
    useEffect(() => {
        const onCreateContextApp = (event: AtollonEvent): void => {
            if (event.detail.isCopy) {
                handleCopy();
            } else {
                // handleMove();
            }
        };

        subscribe(AtollonEvent.CREATE_CONTEXT_APP, onCreateContextApp);

        return () => {
            dispatch(
                updateAppInfo({
                    key: state.appKey,
                    data: {
                        pageView: dataRef.current.pageView,
                        selectedItem: JSON.stringify(
                            dataRef.current.selectedItem
                        ),
                        selectedRow: JSON.stringify(
                            dataRef.current.selectedRow
                        ),
                        showMobileDetailView:
                            dataRef.current.showMobileDetailView,
                        selectedKeys: JSON.stringify(
                            Array.from(dataRef.current.selectedKeys || [])
                        ),
                        queryParams: JSON.stringify(queryParamsRef.current),
                        settingId: dataRef.current.settingId,
                    },
                })
            );
            unsubscribe(AtollonEvent.CREATE_CONTEXT_APP, onCreateContextApp);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, state.appKey]);

    useEffect(() => {
        serviceActivityValueRef.current = activityListKey;
    }, [activityListKey]);

    useEffect(() => {
        actions.setShowLoader(dataLoading);
        // Filter UI should be driven by views/setup readiness, not activities loading.
        actions.setShowFilter(!isViewsLoading);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataLoading, isViewsLoading]);

    useEffect(() => {
        if (dataError) {
            ToastQueue.negative(dataError.message || "Error loading data", {
                timeout: 3000,
            });
        }
    }, [dataError]);

    // Apply layout orientation from custom view only on initial selection
    // This runs once when a custom view is first selected, not on every render
    useEffect(() => {
        // Skip if data is still loading
        if (dataLoading) {
            return;
        }

        // Skip if still initializing
        if (isInitializingRef.current) {
            return;
        }

        // Get current selected custom view ID from ref (check fresh value each time)
        const stateFilter = getFilterCol(queryParamsRef.current).find(
            (col) => col.name === "state"
        );
        const currentViewId = stateFilter?.value
            ? String(stateFilter.value)
            : null;

        // Only apply orientation if:
        // 1. A custom view is selected (currentViewId exists)
        // 2. It's different from the last one we applied (or we haven't applied any yet)
        // This ensures we only set the initial orientation once per custom view selection
        if (
            currentViewId &&
            currentViewId !== lastAppliedCustomViewIdRef.current
        ) {
            const orientationInfo = getLayoutOrientationFromCustomView();

            if (orientationInfo) {
                // Use appropriate orientation based on device type and orientation
                let orientation: "Detail" | "Full" | undefined;

                if (isMobile) {
                    // For mobile devices, use mobileOrientation (backward compatibility) or default
                    orientation = orientationInfo.mobileOrientation || "Full";
                } else if (deviceInfo.isTablet || deviceInfo.isIPad) {
                    // For tablets, use tabletOrientation (applies to both horizontal and vertical)
                    orientation = orientationInfo.tabletOrientation;
                } else {
                    // For desktop, use desktopOrientation
                    orientation = orientationInfo.desktopOrientation;
                }

                if (
                    orientation &&
                    (orientation === "Detail" || orientation === "Full")
                ) {
                    actions.setPageView(orientation);
                    dataRef.current.pageView = orientation;
                    lastAppliedCustomViewIdRef.current = currentViewId;
                } else {
                    // Fallback to defaults if orientation not specified
                    const defaultPageView = isMobile ? "Full" : "Detail";
                    actions.setPageView(defaultPageView);
                    dataRef.current.pageView = defaultPageView;
                    lastAppliedCustomViewIdRef.current = currentViewId;
                }
            }
        } else {
            if (!currentViewId && lastAppliedCustomViewIdRef.current) {
                // Custom view was deselected, reset tracking
                lastAppliedCustomViewIdRef.current = null;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        customViews,
        isMobile,
        dataLoading,
        activities.length,
        getLayoutOrientationFromCustomView,
        actions,
        deviceInfo.isTablet,
        deviceInfo.isIPad,
        deviceInfo.orientation,
    ]);

    useEffect(() => {
        if (
            !isMobile &&
            activities.length > 0 &&
            !selectedKeys?.size &&
            !dataLoading
        ) {
            const first = activities[0];
            actions.setSelectedKeys(new Set([String(first.id)]));
            actions.setSelectedDataRow(first);
            dataRef.current.selectedRow = first;
            dataRef.current.selectedKeys = new Set([String(first.id)]);
            getLayoutRef.current?.(first);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activities, isMobile, selectedKeys, dataLoading]);

    // Handle deviceInfo updates (orientation changes, device type changes)
    useEffect(() => {
        // Force re-render by updating render key
        setRenderKey((prev) => prev + 1);

        // Force component to re-evaluate layout conditions with cleanup
        const timeoutId = setTimeout(() => {
            // Additional re-render after a brief delay to ensure all state updates are processed
            setRenderKey((prev) => prev + 1);
        }, 100);

        // Cleanup timeout on unmount or when dependencies change
        return () => clearTimeout(timeoutId);
    }, [deviceInfo.isTablet, deviceInfo.isIPad, deviceInfo.orientation]);

    const addStatusColor = (statusName: string): string | undefined => {
        const statusObj = activityStatuses.find(
            (obj: ActivityStatus) => obj.name === statusName
        );
        return statusObj?.color;
    };

    const handleOpenCreateViewDialog = useCallback((): void => {
        setCustomViewDialogEditView(null);
        setCustomViewDialogIsDuplicate(false);
        setIsCreateViewDialogOpen(true);
    }, []);

    const handleCloseCustomViewDialog = useCallback((): void => {
        setIsCreateViewDialogOpen(false);
        setCustomViewDialogEditView(null);
        setCustomViewDialogIsDuplicate(false);
    }, []);

    const handleCreateViewSaved = useCallback(
        async (_data: any, viewId?: string): Promise<void> => {
            const wasEditOrDuplicate = customViewDialogEditView != null;
            setIsCreateViewDialogOpen(false);
            setCustomViewDialogEditView(null);
            setCustomViewDialogIsDuplicate(false);

            // If no view ID provided (e.g., from edit mode or legacy callback), just refresh
            if (!viewId) {
                resolvedInitialViewForAppRef.current = null;
                setInitialCustomViewId(undefined);
                await refreshCustomViews();
                if (wasEditOrDuplicate) {
                    resetViewBaselineRef.current?.();
                }
                return;
            }

            // Store the view ID to apply after refresh completes
            pendingCreatedViewIdRef.current = String(viewId);
            pendingViewRetryCountRef.current = 0; // Reset retry counter

            // Re-run initial view resolution after views refresh
            resolvedInitialViewForAppRef.current = null;
            setInitialCustomViewId(undefined);

            // Refresh views list - the useEffect below will handle applying the selection
            await refreshCustomViews();
            if (wasEditOrDuplicate) {
                resetViewBaselineRef.current?.();
            }
        },
        [refreshCustomViews]
    );

    const handleEditView = useCallback((view: CustomView): void => {
        setCustomViewDialogEditView(view);
        setCustomViewDialogIsDuplicate(false);
    }, []);

    const handleDuplicateView = useCallback((view: CustomView): void => {
        const duplicateView: CustomView = {
            ...view,
            id: undefined,
            name: view.name ? `Copy of ${view.name}` : undefined,
            NAME: view.NAME
                ? {
                      TRANSLATION:
                          view.NAME.TRANSLATION?.map((tr) => ({
                              ...tr,
                              text: `Copy of ${tr.text || ""}`,
                          })) || [],
                  }
                : undefined,
        };
        setCustomViewDialogEditView(duplicateView);
        setCustomViewDialogIsDuplicate(true);
    }, []);

    const handleDeleteViewClick = useCallback((view: CustomView): void => {
        setViewToDelete(view);
    }, []);

    const showEmptyViews = useMemo(() => {
        return !isViewsLoading && (customViews?.length ?? 0) === 0;
    }, [customViews?.length, isViewsLoading]);

    const EmptyViewsCTA = useMemo(() => {
        return (
            <Flex
                width="100%"
                height="100%"
                justifyContent="center"
                alignItems="center"
            >
                <AtollonButton
                    designType={AtollonButtonDesignType.PRIMARY}
                    onPress={handleOpenCreateViewDialog}
                >
                    Add View
                </AtollonButton>
            </Flex>
        );
    }, [handleOpenCreateViewDialog]);

    const isNumericValue = (value: unknown): value is number | string => {
        if (value === null || value === undefined) return false;
        if (typeof value === "number") return Number.isFinite(value);
        if (typeof value === "string") {
            const trimmed = value.trim();
            if (trimmed.length === 0) return false;
            return !Number.isNaN(Number(trimmed));
        }
        return false;
    };

    const formatNumericValue = (
        value: number | string
    ): { formatted: string; isNegative: boolean } => {
        const numeric = typeof value === "number" ? value : Number(value);
        if (Number.isNaN(numeric)) {
            return { formatted: String(value), isNegative: false };
        }

        const isNegative = numeric < 0;
        const absoluteValue = Math.abs(numeric);

        // Format with thousands separators and 2 decimal places
        const formatted = new Intl.NumberFormat("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(absoluteValue);

        // Format negative numbers with brackets instead of minus sign
        const finalFormatted = isNegative ? `(${formatted})` : formatted;

        return { formatted: finalFormatted, isNegative };
    };

    /**
     * Reloads service activities with new filter parameters.
     * Resets state and triggers a fresh data load.
     *
     * @param filterParams - The new query parameters to filter activities
     */
    const handleReloadServiceActivities = useCallback(
        (filterParams: QueryParams): void => {
            if (isInitializingRef.current) {
                return;
            }

            // Check if custom view changed - if so, reset tracking to allow orientation to be applied
            const currentStateFilter = getFilterCol(queryParamsRef.current).find(
                (col) => col.name === "state"
            );
            const newStateFilter = getFilterCol(filterParams).find(
                (col) => col.name === "state"
            );
            const currentViewId = currentStateFilter?.value
                ? String(currentStateFilter.value)
                : null;
            const newViewId = newStateFilter?.value
                ? String(newStateFilter.value)
                : null;

            // If custom view changed or was deselected, reset tracking
            if (currentViewId !== newViewId) {
                lastAppliedCustomViewIdRef.current = null;
            }

            // Persist last selected custom view (only when a custom view is selected)
            if (currentViewId !== newViewId && newViewId) {
                if (skipNextViewPersistRef.current) {
                    skipNextViewPersistRef.current = false;
                } else {
                    // Fire-and-forget: persistence must not block UI update
                    void persistLastViewId(newViewId);
                }
            }

            // Reset selection state so first item of new list gets selected
            actions.setSelectedKeys(undefined);
            actions.setSelectedDataRow(null);
            actions.setSelectedItem(null);
            actions.setDefinition(null);
            dataRef.current.selectedItem = undefined;
            dataRef.current.selectedRow = undefined;
            dataRef.current.selectedKeys = undefined;
            dataRef.current.queryParams = JSON.stringify(filterParams);
            queryParamsRef.current = filterParams;
            reload(filterParams);
        },
        [reload, actions, persistLastViewId]
    );

    const handleDeleteViewConfirm = useCallback(async (): Promise<void> => {
        if (!viewToDelete?.id) return;
        const wasSelected =
            getFilterCol(queryParamsRef.current).find(
                (col) => col.name === "state"
            )?.value != null &&
            String(
                getFilterCol(queryParamsRef.current).find(
                    (col) => col.name === "state"
                )?.value
            ) === String(viewToDelete.id);

        setIsDeletingView(true);
        try {
            const response = await featureService.deleteCustomViews(
                new DeleteCustomViewsRequest(store.Server, store.SessionId, [
                    { id: viewToDelete.id },
                ])
            );
            if (response?.result === "OK") {
                if (wasSelected) {
                    const updatedParams: QueryParams = {
                        ...queryParamsRef.current,
                        FILTER: {
                            COL: getFilterCol(queryParamsRef.current).filter(
                                (col) => col.name !== "state"
                            ),
                        },
                    };
                    delete (updatedParams as any).FIELDS;
                    delete (updatedParams as any).ORDER;
                    handleReloadServiceActivities(updatedParams);
                }
                await refreshCustomViews();
            }
        } finally {
            setIsDeletingView(false);
            setViewToDelete(null);
        }
    }, [
        viewToDelete,
        featureService,
        store.Server,
        store.SessionId,
        refreshCustomViews,
        getFilterCol,
        handleReloadServiceActivities,
    ]);

    // Apply pending view selection after views are refreshed
    useEffect(() => {
        if (!pendingCreatedViewIdRef.current || isViewsLoading || !customViews) {
            return;
        }

        const pendingViewId = pendingCreatedViewIdRef.current;
        const newView = customViews.find(
            (v) => String(v.id ?? "") === pendingViewId
        );

        if (newView) {
            // Clear the pending ID first to prevent re-triggering
            pendingCreatedViewIdRef.current = null;

            // Apply the view selection by building query params and reloading
            const queryParams = applyViewSelection(newView);
            
            // Skip persistence on initial apply (similar to initial view selection)
            skipNextViewPersistRef.current = true;
            
            // Apply the view by calling handleReloadServiceActivities
            handleReloadServiceActivities(queryParams);
        } else {
            // View not found yet - increment retry counter and wait for next render when views update
            pendingViewRetryCountRef.current += 1;
            
            // Safety: Clear pending ID after too many retries (view might not exist)
            if (pendingViewRetryCountRef.current > 10) {
                console.warn(
                    `Newly created view with ID ${pendingViewId} not found after ${pendingViewRetryCountRef.current} retries. View may not exist.`
                );
                pendingCreatedViewIdRef.current = null;
                pendingViewRetryCountRef.current = 0;
            }
            // Otherwise, don't clear pending ID - let it retry on next render when customViews updates
        }
    }, [customViews, isViewsLoading, applyViewSelection, handleReloadServiceActivities]);

    /**
     * Handles double-click on a table row to open the finder app.
     *
     * @param selection - The selection object from React Aria TableView
     */
    const handleTableRowdblClick = (key: Key): void => {
        if (key) {
            invokeFinderApp(String(key));
        }
    };

    /**
     * Handles click on a table row to select and load its details.
     *
     * @param selection - The selection object from React Aria TableView
     */
    const handleTableRowClick = useCallback(
        (selection: Selection): void => {
            const selectedKey =
                selection === "all" ? undefined : Array.from(selection)[0];
            if (!selectedKey) {
                actions.resetSelection();
                return;
            }
            const keyString = String(selectedKey);
            actions.setSelectedKeys(
                selection === "all" ? undefined : new Set([keyString])
            );
            const selectedRow = activities.find(
                (item: ActivityItem) => String(item.id) === keyString
            );
            if (!selectedRow) {
                // Consider using a reducer pattern:
                actions.resetSelection();
                return;
            }
            actions.setSelectedDataRow(selectedRow);
            getLayoutRef.current?.(selectedRow);
        },
        [activities, actions]
    );

    /**
     * Handles click on a list row (card view) to select and load its details.
     *
     * @param selection - The selection object from React Aria ListBox
     */
    const handleListRowClick = (selection: Selection): void => {
        //console.log('handleListRowClick',handleListRowClick);
        const selectedKey =
            selection === "all" ? undefined : Array.from(selection)[0];
        if (!selectedKey || selectedKeys?.has(selectedKey)) {
            return;
        }

        actions.setShowLoaderDetail(true);
        actions.setSelectedKeys(
            selection === "all" ? undefined : new Set([selectedKey])
        );
        actions.setPageView("Full");
        dataRef.current.pageView = "Full";

        const keyString = String(selectedKey);
        const selectedRow = activities.find(
            (item: ActivityItem) => String(item.id) === keyString
        );
        if (!selectedRow) {
            actions.resetSelection();
            return;
        }
        actions.setSelectedDataRow(selectedRow);
        actions.setSelectedKeys(new Set([selectedRow.id]));
        dataRef.current.selectedRow = selectedRow;
        dataRef.current.selectedKeys =
            selection === "all" ? undefined : new Set([selectedKey]);
        getLayoutRef.current?.(selectedRow);
    };

    const scrollPositionRef = useRef<number>(0);
    /**
     * Handles click on a mobile list row to select and show detail view.
     *
     * @param selection - The selection object from React Aria ListBox
     */
    const handleRowClickMobile = (selection: Selection): void => {
        console.log("selectedDataRow", selectedDataRow);
        const listContainer = document.getElementById(
            "listActivityCardsMobile"
        );
        if (listContainer) {
            scrollPositionRef.current = listContainer.scrollTop;
        }
        const nameSearchExists = getFilterCol(queryParamsRef.current).find(
            (col: FilterColumn) => col.name === "nameSearch"
        );
        if (nameSearchExists) {
            searchedValueRef.current =
                typeof nameSearchExists.value === "string"
                    ? nameSearchExists.value
                    : "";
        }
        const selectedKey =
            selection === "all" ? undefined : Array.from(selection)[0];
        const selectedRow = selectedKey
            ? activities.find((item: ActivityItem) => item.id === selectedKey)
            : activities[0];
        if (!selectedRow) {
            actions.setSelectedDataRow({} as ActivityItem);
            actions.setDefinition(null);
            return;
        }
        actions.setSelectedDataRow(selectedRow);
        const keyString = selectedKey ? String(selectedKey) : undefined;
        actions.setSelectedKeys(
            selection === "all" ? undefined : new Set([keyString!])
        );
        dataRef.current.selectedRow = selectedRow;
        dataRef.current.selectedKeys =
            selection === "all" ? undefined : new Set([keyString!]);

        getLayoutRef.current?.(selectedRow);
        actions.setShowMobileDetailView(true);
        dataRef.current.showMobileDetailView = true;
    };

    /**
     * Handles page view mode change (Full/Detail).
     * Maps 'Grid' to 'Detail' (table view) and 'Full' to 'Full' (card + detail view).
     *
     * @param view - The new view mode ('Grid', 'Full', or 'Detail')
     */
    const handleViewChange = (view: string): void => {
        const nextView: "Full" | "Detail" =
            view === "Grid" ? "Detail" : view === "Detail" ? "Detail" : "Full";
        actions.setPageView(nextView);
        dataRef.current.pageView = nextView;
    };

    /**
     * Gets a React element displaying the status with its color icon.
     *
     * @param statusName - The name of the status to display
     * @returns A React element with status icon and name
     */
    const getStatusColor = (statusName: string): React.ReactElement => {
        const statusObj = activityStatuses.find(
            (obj: ActivityStatus) => obj.name === statusName
        );
        return (
            <>
                <CircleFill
                    style={{ paddingRight: "10px" }}
                    size={"18px"}
                    color={statusObj?.color}
                />{" "}
                {statusName}
            </>
        );
    };

    /**
     * Fetches and sets the layout definition for a selected activity row.
     * Handles request cancellation to prevent race conditions.
     *
     * @param selectedRow - The selected activity item, or null to clear layout
     * @returns Promise that resolves when layout is loaded or cleared
     */
    const loadLayout = useCallback(
        async (selectedRow: ActivityItem | null): Promise<void> => {
            if (!selectedRow?.id) {
                actions.setDefinition(null);
                actions.setSelectedItem(null);
                actions.setShowLoaderDetail(false);
                return;
            }

            const requestId = ++layoutRequestIdRef.current;
            actions.setShowLoaderDetail(true);

            try {
                const layoutData = await fetchActivityLayout(selectedRow);
                if (layoutRequestIdRef.current !== requestId) return;

                if (!layoutData) {
                    actions.setDefinition(null);
                    actions.setSelectedItem(null);
                    return;
                }

                const parent = {
                    id: Number(
                        (layoutData.activity as { project: string | number })
                            .project
                    ),
                    type: FPADataTypes.PROJECT,
                    item_type_id: Number(
                        (layoutData.project as { projectType: number })
                            .projectType
                    ),
                };

                const resSelectedItem =
                    FPADataFactory.createFromActivityAdvanced(
                        { ...selectedRow, id: Number(selectedRow.id) } as any,
                        activityStatusMapping,
                        FPADataTypes.PROJECT,
                        parent
                    );
                if (layoutRequestIdRef.current !== requestId) return;

                actions.setSelectedItem(resSelectedItem);
                dataRef.current.selectedItem = resSelectedItem;
                actions.setDefinition(layoutData.layoutObject as any);
            } catch (err) {
                if (layoutRequestIdRef.current === requestId) {
                    actions.setDefinition(null);
                }
                console.error("getlayout error: ", err);
            } finally {
                if (layoutRequestIdRef.current === requestId) {
                    actions.setShowLoaderDetail(false);
                }
            }
        },
        [actions, activityStatusMapping, fetchActivityLayout]
    );

    useEffect(() => {
        getLayoutRef.current = loadLayout;
    }, [loadLayout]);

    const Loader = () => (
        <Flex
            width="100%"
            justifyContent="center"
            marginTop={10}
            UNSAFE_style={{ backgroundColor: "inherit" }}
        >
            <ProgressCircle aria-label="Loading…" isIndeterminate />
        </Flex>
    );

    const NoResultsMessage = () => (
        <Flex
            direction="row"
            width="100%"
            alignContent="center"
            justifyContent="center"
        >
            <IllustratedMessage>
                <NotFound />
                <Heading>
                    {t("no_results", { ns: "layout_components" })}
                </Heading>
                <Content>
                    {t("try_another_search", { ns: "layout_components" })}
                </Content>
            </IllustratedMessage>
        </Flex>
    );

    /**
     * Calculates height with offset for UI components.
     *
     * @param baseHeight - The base height value
     * @returns Height string in pixels
     */
    const getHeight = (baseHeight: number): string => {
        return `${baseHeight - 120}px`;
    };

    /**
     * Gets the standard width for card components.
     *
     * @returns Width string as percentage
     */
    const getWidth = (): string => {
        return `95%`;
    };

    const detailStyle = {
        height: getHeight(screenSize.height - 20),
    };

    const cardStyle = {
        height: getHeight(screenSize.height),
        width: getWidth(),
        paddingBottom: "15px",
    };

    const filterStyle = {
        height: getHeight(screenSize.height + 60),
        overflow: "hidden auto",
    };

    /**
     * Opens the add new activity dialog.
     */
    const handleAddNew = (): void => {
        actions.setShowAdd(true);
        modeRef.current = "new";
    };

    /**
     * Closes the add new activity dialog and optionally reloads the list.
     *
     * @param reload_flag - Whether to reload the activity list after closing
     */
    const handleAddNewClose = (reload_flag: boolean = false): void => {
        if (reload_flag) loadMore();
        actions.setShowAdd(false);
    };

    /**
     * Closes the add new activity dialog and reloads the list.
     */
    const handleAddNewCloseDismiss = (): void => {
        actions.setShowAdd(false);
        loadMore();
    };

    /**
     * Opens the add new activity dialog in copy mode.
     */
    const handleCopy = (): void => {
        if (selectedItem) {
            actions.setShowAdd(true);
            modeRef.current = "copy";
        } else {
            ToastQueue.negative(
                t("no_selected_item", { ns: "layout_components" }),
                { timeout: 3000 }
            );
        }
    };

    /**
     * Opens the move dialog for the selected item.
     *
     * @param _selectedItem - The item to move, or null
     */
    const handleMove = (_selectedItem: FPAData | null): void => {
        if (_selectedItem) {
            actions.setIsMoveDialogOpen(true);
            actions.setSelectedItem(_selectedItem);
        } else {
            ToastQueue.negative(
                t("no_selected_item", { ns: "layout_components" }),
                { timeout: 3000 }
            );
        }
    };

    /**
     * Opens the delete confirmation dialog.
     *
     * @param item - The item to delete, or null
     */
    const handleDelete = (item: FPAData | null): void => {
        if (item === null) return;
        actions.setIsDeleteDialogOpen(true);
    };

    /**
     * Confirms and executes the delete operation for the selected activity.
     *
     * @returns Promise that resolves when delete is complete
     */
    const handleConfirmDelete = async (): Promise<void> => {
        if (selectedItem === null) return;
        actions.setIsDeleteDialogOpen(false);
        if (selectedItem.type === FPADataTypes.ACTIVITY) {
            const res = await projectService.deleteActivity(
                new DeleteActivityRequest(
                    store.Server,
                    store.SessionId,
                    selectedItem.id
                )
            );
            if (res.EXCEPTION) {
                ToastQueue.negative(res.EXCEPTION.message, { timeout: 3000 });
            } else {
                loadMore();
            }
        }
    };

    /**
     * Triggers delete confirmation for the currently selected activity.
     */
    const handleDeleteActivity = (): void => {
        if (selectedItem === null) return;
        handleDelete(selectedItem);
    };

    /**
     * Closes the move dialog and optionally reloads the list if item was moved.
     *
     * @param moved - Whether the item was successfully moved
     */
    const handleMoveClose = (moved: boolean): void => {
        if (moved) {
            loadMore();
        }
        actions.setIsMoveDialogOpen(false);
    };

    const handleEdit = (): void => {
        if (selectedItem) {
            invokeFinderApp(String(selectedItem.id));
        }
    };

    /**
     * Handles Load More/Show Less toggle functionality.
     * When expanded, shows less items. When not expanded and has more, loads more items.
     */
    const handleLoadMoreToggle = useCallback((): void => {
        if (isExpanded) {
            showLess();
        } else if (hasMore && !dataLoading) {
            loadMore();
        }
    }, [isExpanded, hasMore, dataLoading, loadMore, showLess]);

    /**
     * Scrolls to the previously saved scroll position in the mobile list view.
     * Used to restore scroll position when returning from detail view.
     */
    const scrollToSelected = useCallback((): void => {
        setTimeout(() => {
            const listContainer = document.getElementById(
                "listActivityCardsMobile"
            );
            if (listContainer && scrollPositionRef.current > 0) {
                listContainer.scrollTo({
                    top: scrollPositionRef.current,
                    behavior: "smooth",
                });
                //console.log('Restored scroll position:', scrollPositionRef.current);
            }
        }, 1000);
    }, []);

    // Store scrollToSelected in ref for use in loadServiceActivities (which is defined earlier)
    useEffect(() => {
        scrollToSelectedRef.current = scrollToSelected;
    }, [scrollToSelected]);

    // Component system attributes for consistency
    const systemAttributes = {
        onPrint: (item: FPAData | null) => console.log("onPrint", item),
        onEdit: (item: FPAData | null) => console.log("onEdit", item),
        onDelete: (item: FPAData | null) => handleDelete(item),
        onChat: (item: FPAData | null) => console.log("onChat", item),
        onConfig: (item: FPAData | null, type: string) =>
            console.log("onConfig", item, type),
    };

    /**
     * Closes the mobile detail view and returns to the list view.
     * Resets selection state and restores scroll position.
     */
    const handleMobileDetailClose = (): void => {
        actions.setShowMobileDetailView(false);
        dataRef.current.showMobileDetailView = false;
        actions.setSelectedKeys(undefined);
        dataRef.current.selectedItem = undefined;
        dataRef.current.selectedRow = undefined;
        actions.setSelectedDataRow(null);
        getLayoutRef.current?.(null);
        scrollToSelectedRef.current?.();
    };

    /**
     * Opens the finder app for the selected item.
     */
    const handleOpenFinder = (): void => {
        if (selectedItem?.id) {
            invokeFinderApp(String(selectedItem.id));
        }
        actions.setShowMobileDetailView(false);
        dataRef.current.showMobileDetailView = false;
    };

    // Create mobile layout component
    const mobileLayout = (
        <MobileLayout
            showDetailView={showMobileDetailView}
            onBack={handleMobileDetailClose}
        >
            {{
                list: (
                    <MobileListView
                        activities={activities}
                        selectedKeys={selectedKeys}
                        isLoading={dataLoading}
                        onActivitySelect={handleRowClickMobile}
                        onLoadMore={
                            isExpanded
                                ? undefined
                                : hasMore
                                ? loadMore
                                : undefined
                        }
                        onShowLess={showLess}
                        isExpanded={isExpanded}
                        hasMore={hasMore}
                        activityStatusList={activityStatuses}
                        onAddNew={handleAddNew}
                        onFilterChange={handleReloadServiceActivities}
                        filterParams={queryParamsRef.current}
                        searchedText={searchedValueRef.current}
                        fpaTypeCategory={serviceActivityValueRef.current}
                        deviceInfo={deviceInfo}
                        customViews={customViews}
                        hasMoreCustomViews={hasMoreCustomViews}
                        onLoadMoreCustomViews={loadMoreCustomViews}
                        onRefreshCustomViews={refreshCustomViews}
                        formSettings={formSettings}
                        pageView={pageView}
                        mergedColumns={mergedColumns}
                        onTableRowClick={handleTableRowClick}
                        onTableRowDblClick={handleTableRowdblClick}
                        getStatusColor={getStatusColor}
                        isNumericValue={isNumericValue}
                        formatNumericValue={formatNumericValue}
                        selectedApp={selectedApp}
                        initialViewId={initialCustomViewId}
                        onCreateView={handleOpenCreateViewDialog}
                        onEditView={handleEditView}
                        onDuplicateView={handleDuplicateView}
                        onDeleteViewClick={handleDeleteViewClick}
                        resetBaselineRef={resetViewBaselineRef}
                    />
                ),
                detail: (
                    <MobileDetailView
                        selectedItem={selectedItem}
                        selectedDataRow={selectedDataRow}
                        definition={definition}
                        isLoading={showLoaderDetail}
                        activityStatusList={activityStatuses}
                        onBack={handleMobileDetailClose}
                        onOpenFinder={handleOpenFinder}
                        systemAttributes={systemAttributes}
                        getStatusColor={addStatusColor}
                    />
                ),
            }}
        </MobileLayout>
    );

    return (
        <div key={renderKey}>
            <ServiceActivitiesLayout
                pageView={pageView}
                showMobileDetailView={showMobileDetailView}
                deviceInfo={deviceInfo}
                onViewChange={handleViewChange}
                mobileLayout={mobileLayout}
                showAdd={showAdd}
                filterSlot={
                    showFilter ? (
                        <FilterComponent
                            key={selectedApp || 'default'}
                            onFilterChange={
                                handleReloadServiceActivities as any
                            }
                            filterParams={queryParamsRef.current}
                            filterStyle={filterStyle}
                            formSettings={formSettings}
                            customViews={customViews}
                            hasMoreCustomViews={hasMoreCustomViews}
                            onLoadMoreCustomViews={loadMoreCustomViews}
                            onRefreshCustomViews={refreshCustomViews}
                            fpaTypeCategory={serviceActivityValueRef.current}
                            initialViewId={initialCustomViewId}
                            onCreateView={handleOpenCreateViewDialog}
                            onEditView={handleEditView}
                            onDuplicateView={handleDuplicateView}
                            onDeleteViewClick={handleDeleteViewClick}
                            resetBaselineRef={resetViewBaselineRef}
                        />
                    ) : (
                        <Flex
                            height={"100%"}
                            justifyContent={"center"}
                            alignItems={"center"}
                            UNSAFE_style={{ backgroundColor: "white" }}
                        >
                            <Loader />
                        </Flex>
                    )
                }
                headerSlot={
                    <HeaderComponent
                        onPageViewChange={handleViewChange}
                        autoChanged={pageView === "Detail" ? "Grid" : "Full"}
                        filterParams={queryParamsRef.current}
                        onSearchChange={handleReloadServiceActivities as any}
                        fpaTypeCategory={serviceActivityValueRef.current}
                        onAddNew={handleAddNew}
                        onCopy={handleCopy}
                        onDelete={handleDeleteActivity}
                        onEdit={handleEdit}
                    />
                }
                tabletFilterSlot={
                    <FilterComponent
                        key={selectedApp || 'default'}
                        onFilterChange={handleReloadServiceActivities as any}
                        filterParams={queryParamsRef.current}
                        formSettings={formSettings}
                        customViews={customViews}
                        hasMoreCustomViews={hasMoreCustomViews}
                        onLoadMoreCustomViews={loadMoreCustomViews}
                        onRefreshCustomViews={refreshCustomViews}
                        fpaTypeCategory={serviceActivityValueRef.current}
                        initialViewId={initialCustomViewId}
                        onCreateView={handleOpenCreateViewDialog}
                        onEditView={handleEditView}
                        onDuplicateView={handleDuplicateView}
                        onDeleteViewClick={handleDeleteViewClick}
                        resetBaselineRef={resetViewBaselineRef}
                    />
                }
                cardSlot={
                    showEmptyViews ? null : showLoader ? (
                        <Flex
                            width={"100%"}
                            height={"100%"}
                            position={"absolute"}
                            justifyContent={"center"}
                            alignItems={"center"}
                            UNSAFE_style={{ backgroundColor: "white" }}
                        >
                            <Loader />
                        </Flex>
                    ) : activities?.length > 0 ? (
                        <>
                            <ListBox
                                autoFocus
                                id="listActivityCards"
                                selectionMode="single"
                                onSelectionChange={handleListRowClick}
                                marginStart={"15px"}
                                marginTop={"13px"}
                                minHeight={"130px"}
                                maxHeight={"100vh"}
                                selectedKeys={selectedKeys}
                                aria-label="Service Activities Cards"
                                items={activities}
                                isLoading={dataLoading}
                                onLoadMore={
                                    isExpanded
                                        ? undefined
                                        : hasMore
                                        ? loadMore
                                        : undefined
                                }
                                UNSAFE_style={cardStyle}
                            >
                                {(item) => (
                                    <Item key={item.id} textValue={item.name}>
                                        <CardComponent
                                            item={item}
                                            activityStatusList={
                                                activityStatuses
                                            }
                                        />
                                    </Item>
                                )}
                            </ListBox>
                            {(isExpanded || (hasMore && !isExpanded)) && (
                                <Flex
                                    width="100%"
                                    justifyContent="center"
                                    marginTop="size-100"
                                    marginStart="size-200"
                                >
                                    <Link
                                        onPress={handleLoadMoreToggle}
                                        isQuiet
                                    >
                                        {isExpanded
                                            ? t("show_less", {
                                                  ns: "layout_components",
                                                  defaultValue: "Show Less",
                                              })
                                            : t("load_more", {
                                                  ns: "layout_components",
                                                  defaultValue: "Load More",
                                              })}
                                    </Link>
                                </Flex>
                            )}
                        </>
                    ) : (
                        <View marginTop={"13px"}>
                            <NoResultsMessage />
                        </View>
                    )
                }
                detailSlot={
                    showEmptyViews ? (
                        EmptyViewsCTA
                    ) : (activities?.length ?? 0) > 0 ? (
                        <View
                            backgroundColor={"static-white"}
                            borderRadius={"large"}
                            padding={"size-100"}
                            paddingTop={"0"}
                            marginTop={"17px"}
                            width={"99%"}
                            UNSAFE_style={detailStyle}
                            UNSAFE_className={styles.detailView}
                        >
                            {definition &&
                                (showLoaderDetail ? (
                                    <Loader />
                                ) : (
                                    <View
                                        height="100%"
                                        UNSAFE_className={styles.detailsHolder}
                                        UNSAFE_style={{
                                            overflow: "hidden auto",
                                        }}
                                        marginStart={"10px"}
                                    >
                                        {selectedItem &&
                                            activities?.length > 0 && (
                                                <>
                                                    <View
                                                        UNSAFE_className={
                                                            styles.service_status_bar
                                                        }
                                                        UNSAFE_style={{
                                                            backgroundColor:
                                                                addStatusColor(
                                                                    selectedDataRow?.customStateName ??
                                                                        ""
                                                                ),
                                                        }}
                                                    />
                                                    <View height={"2px"}>
                                                        &nbsp;
                                                    </View>
                                                    <ComponentFactory
                                                        selectedItem={
                                                            selectedItem
                                                        }
                                                        definition={definition}
                                                        systemAttributes={
                                                            systemAttributes as unknown as any
                                                        }
                                                    />
                                                </>
                                            )}
                                    </View>
                                ))}
                        </View>
                    ) : (
                        <NoResultsMessage />
                    )
                }
                tableSlot={
                    showEmptyViews ? (
                        EmptyViewsCTA
                    ) : showLoader ? (
                        <Loader />
                    ) : (
                        <View
                            position={"relative"}
                            overflow={"auto"}
                            height={"88vh"}
                            width={"100%"}
                            UNSAFE_className={styles.service_activities}
                        >
                            {visibleGridColumns?.length > 0 &&
                            activities?.length > 0 ? (
                                <>
                                    <TableView
                                        UNSAFE_className="tblLayoutComponent"
                                        aria-label="Service Activities"
                                        onSelectionChange={handleTableRowClick}
                                        selectionMode="single"
                                        selectedKeys={selectedKeys}
                                        selectionStyle="highlight"
                                        width={{
                                            base: "100%",
                                            L: "100%",
                                            M: "1100px",
                                        }}
                                        minHeight={"200px"}
                                        height={"100%"}
                                        onAction={handleTableRowdblClick}
                                    >
                                        <TableHeader columns={visibleGridColumns}>
                                            {(column: any) => (
                                                <Column
                                                    showDivider
                                                    key={column.key}
                                                >
                                                    {column.name}
                                                </Column>
                                            )}
                                        </TableHeader>
                                        <TableBody
                                            items={activities}
                                            loadingState={loadingState}
                                            onLoadMore={
                                                hasMore &&
                                                loadingState === "idle"
                                                    ? loadMore
                                                    : undefined
                                            }
                                        >
                                            {(item: any) => (
                                                <Row>
                                                    {(columnKey) => {
                                                        const cellValue =
                                                            item[columnKey];
                                                        const isNumeric =
                                                            isNumericValue(
                                                                cellValue
                                                            ) &&
                                                            columnKey !==
                                                                "customStateName";
                                                        let numericFormatResult: {
                                                            formatted: string;
                                                            isNegative: boolean;
                                                        } | null = null;

                                                        if (isNumeric) {
                                                            numericFormatResult =
                                                                formatNumericValue(
                                                                    cellValue
                                                                );
                                                        }

                                                        return (
                                                            <Cell>
                                                                <View
                                                                    UNSAFE_className={clsx(
                                                                        {
                                                                            [styles.numericCell]:
                                                                                isNumeric,
                                                                            [styles.negativeNumericCell]:
                                                                                numericFormatResult?.isNegative,
                                                                        }
                                                                    )}
                                                                >
                                                                    {(() => {
                                                                        if (
                                                                            moment(
                                                                                cellValue,
                                                                                moment.ISO_8601,
                                                                                true
                                                                            ).isValid()
                                                                        ) {
                                                                            return moment(
                                                                                cellValue
                                                                            ).format(
                                                                                "MM/DD/YYYY | hh:mm A"
                                                                            );
                                                                        }
                                                                        if (
                                                                            columnKey ===
                                                                            "customStateName"
                                                                        ) {
                                                                            return getStatusColor(
                                                                                cellValue
                                                                            );
                                                                        }
                                                                        if (
                                                                            isNumeric &&
                                                                            numericFormatResult
                                                                        ) {
                                                                            return numericFormatResult.formatted;
                                                                        }
                                                                        return cellValue;
                                                                    })()}
                                                                </View>
                                                            </Cell>
                                                        );
                                                    }}
                                                </Row>
                                            )}
                                        </TableBody>
                                    </TableView>
                                </>
                            ) : (
                                <NoResultsMessage />
                            )}
                        </View>
                    )
                }
            />

            {/* Single Custom View Dialog: create, edit, duplicate */}
            <DialogContainer
                isDismissable={false}
                onDismiss={handleCloseCustomViewDialog}
            >
                {(isCreateViewDialogOpen || customViewDialogEditView != null) && (
                    <CustomViewDialog
                        formSettings={formSettings || []}
                        applicationId={selectedApp || ""}
                        onCancel={handleCloseCustomViewDialog}
                        onSave={handleCreateViewSaved}
                        fpaTypeCategory={serviceActivityValueRef.current}
                        editView={customViewDialogEditView ?? undefined}
                        isDuplicate={customViewDialogIsDuplicate}
                    />
                )}
            </DialogContainer>

            {/* View delete confirmation */}
            <DialogContainer
                onDismiss={() => setViewToDelete(null)}
            >
                {viewToDelete && (
                    <AlertDialog
                        title={t("delete_view", {
                            ns: "layout_components",
                            defaultValue: "Delete View",
                        })}
                        variant="destructive"
                        primaryActionLabel={t("delete", {
                            ns: "layout_components",
                            defaultValue: "Delete",
                        })}
                        cancelLabel={t("cancel", {
                            ns: "layout_components",
                            defaultValue: "Cancel",
                        })}
                        onPrimaryAction={handleDeleteViewConfirm}
                        onCancel={() => setViewToDelete(null)}
                        isPrimaryActionDisabled={isDeletingView}
                    >
                        {t("delete_view_confirmation", {
                            ns: "layout_components",
                            defaultValue:
                                "Are you sure you want to delete this view? This action cannot be undone.",
                        })}
                    </AlertDialog>
                )}
            </DialogContainer>

            {/* Dialog Components */}
            <DataProvider>
                <DialogContainer onDismiss={handleAddNewCloseDismiss}>
                    {showAdd && (
                        <ContextNewDialog
                            parentItem={selectedItem}
                            onClose={handleAddNewClose}
                            additionalMode={true}
                            additionalData={{
                                fpaTypeCategory:
                                    serviceActivityValueRef.current,
                                fpaType: "ACTIVITY",
                                haveMasterActivity: false,
                            }}
                            mode={modeRef.current}
                        />
                    )}
                    {isMoveDialogOpen && (
                        <ContextMoveDialog
                            onClose={handleMoveClose}
                            parentItem={null}
                            selectedItem={selectedItem}
                            showActivities={false}
                            showProjects={true}
                            showFolders={true}
                            relatedToActivityType={selectedItem?.item_type_id}
                            selectedItemType={FPADataTypes.PROJECT}
                        />
                    )}
                </DialogContainer>
            </DataProvider>

            <DeleteConfirmationDialog
                isOpen={isDeleteDialogOpen}
                onClose={() => actions.setIsDeleteDialogOpen(false)}
                onConfirm={handleConfirmDelete}
                itemName={selectedItem?.title}
            />
        </div>
    );
}
